import { DownloadOutlined, EyeOutlined, FilePdfOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, message, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type WorkflowTemplate } from '@/api/workflows';
import { ICON_BTN_PROPS } from '@/components/iconButton';
import { handleApiError } from '@/lib/handleApiError';

import { type FieldValue } from './FieldRenderer';
import { fileIcon } from './fileIcon';
import { downloadFileToDevice, openFileInNewTab } from './filePreviewActions';
import { buildFilledPdfBlobUrl, collectPdfDocs, triggerBlobDownload } from './pdfPreviewActions';
import type { UploadedFilesStore } from './uploadedFiles';
import { flattenUploadedFiles } from './uploadedFiles';

const { Paragraph, Text } = Typography;

/**
 * Final-step UI for `PdfGeneration`. Lists every PDF that would be generated
 * for the advisor — one row per `StepFileEntry` configured across any
 * `PdfFileUpload` steps.
 *
 * On mount every entry is pre-filled via pdf-lib against the current answers,
 * and the resulting Blob URLs are cached in a ref so per-row Preview /
 * Download are instant. If the advisor navigates back and edits answers,
 * remounting the step re-runs generation. Rows without an uploaded template
 * skip generation and fall back to the existing stub message on click.
 */
/**
 * FormShell-owned cache. Keyed by `StepFileEntry.id`; each entry pins the
 * `values` reference the URL was built from so we can detect when a fresh
 * fill is needed. FormShell's own unmount revokes every URL — this file
 * never revokes on remount.
 */
export type PdfCache = Map<string, { values: Record<string, FieldValue>; url: string }>;

export function PdfGenerationStep({
  template,
  language,
  values,
  cache,
  uploadedFiles,
}: {
  template: WorkflowTemplate;
  language: 'en' | 'fr';
  values: Record<string, FieldValue>;
  cache: PdfCache;
  uploadedFiles: UploadedFilesStore;
}) {
  const { t } = useTranslation();
  const docs = useMemo(
    () => collectPdfDocs(template, language, values),
    [template, language, values],
  );
  // Flatten every advisor-uploaded file (named entries + optional bucket)
  // so the list rendering below is one uniform ul. Named entries expose
  // the entry label; bucket files fall back to the filename.
  const uploadedRows = useMemo(() => {
    const entryMeta: Record<string, { label: string; filenameTemplate?: string | null }> = {};
    for (const step of template.steps) {
      if (step.type !== 'AdditionalDocuments') continue;
      for (const e of step.additionalDocumentEntries ?? []) {
        entryMeta[e.id] = { label: e.label, filenameTemplate: e.filenameTemplate };
      }
    }
    return flattenUploadedFiles(uploadedFiles, entryMeta, values, template);
  }, [template, uploadedFiles, values]);

  // Pre-generated Blob URL per entryId. `null` = generation failed (fall
  // back to on-click retry); missing key = still generating (or never
  // eligible if previewKey is null). Seed from cache on mount so revisiting
  // the step with unchanged answers renders instantly, no spinner blink.
  const [ready, setReady] = useState<Record<string, string | null>>(() => {
    const initial: Record<string, string | null> = {};
    for (const [entryId, entry] of cache) {
      if (entry.values === values) initial[entryId] = entry.url;
    }
    return initial;
  });

  useEffect(() => {
    // Local cancel flag so a stale generation run (values/template changed
    // mid-flight) can't overwrite the newer cycle's state.
    let cancelled = false;

    void (async () => {
      const eligible = docs.filter((d) => d.previewKey);
      await Promise.all(
        eligible.map(async (d) => {
          const existing = cache.get(d.entryId);
          if (existing && existing.values === values) {
            // Cache hit — surface immediately so the UI never shows a
            // spinner on a step revisit where nothing changed.
            setReady((prev) =>
              prev[d.entryId] === existing.url ? prev : { ...prev, [d.entryId]: existing.url },
            );
            return;
          }
          try {
            const blobUrl = await buildFilledPdfBlobUrl(
              template.id,
              template,
              d.previewKey!,
              values,
              d.entryId,
            );
            if (cancelled) {
              URL.revokeObjectURL(blobUrl);
              return;
            }
            // Values changed since last fill — revoke the stale URL before
            // replacing so we never leak more than one per entry.
            if (existing) URL.revokeObjectURL(existing.url);
            cache.set(d.entryId, { values, url: blobUrl });
            setReady((prev) => ({ ...prev, [d.entryId]: blobUrl }));
          } catch {
            if (cancelled) return;
            setReady((prev) => ({ ...prev, [d.entryId]: null }));
          }
        }),
      );
    })();

    return () => {
      cancelled = true;
      // NO revoke here — FormShell owns lifecycle. Revoking on child unmount
      // would blow away the cache we're trying to preserve.
    };
  }, [docs, template, values, cache]);

  if (docs.length === 0 && uploadedRows.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Paragraph style={{ marginBottom: 0, color: '#374151', fontSize: 14 }}>
          {t('formRenderer.pdfGeneration.empty')}
        </Paragraph>
      </div>
    );
  }

  // Preview: prefer cached URL; regenerate on demand if cache missing or
  // errored (transient S3 hiccup shouldn't dead-end the row).
  const handlePreview = async (label: string, entryId: string, key: string | null) => {
    if (!key) {
      void message.info(t('formRenderer.pdfGeneration.stubPreview', { name: label }));
      return;
    }
    const cached = ready[entryId];
    if (cached) {
      window.open(cached, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const blobUrl = await buildFilledPdfBlobUrl(template.id, template, key, values, entryId);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
    } catch (err) {
      handleApiError(err, t, t('formRenderer.pdfGeneration.previewFailed'));
    }
  };

  const handleDownload = async (
    label: string,
    fileName: string,
    entryId: string,
    key: string | null,
  ) => {
    if (!key) {
      void message.info(t('formRenderer.pdfGeneration.stubDownload', { name: label }));
      return;
    }
    const cached = ready[entryId];
    if (cached) {
      // Reuse the pre-gen URL — download triggers reads it into the browser
      // download stream so we don't revoke this instance's cache; the effect
      // cleanup will handle it on unmount / regeneration.
      const a = document.createElement('a');
      a.href = cached;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    try {
      const blobUrl = await buildFilledPdfBlobUrl(template.id, template, key, values, entryId);
      triggerBlobDownload(blobUrl, fileName);
    } catch (err) {
      handleApiError(err, t, t('formRenderer.pdfGeneration.previewFailed'));
    }
  };

  // Per-uploaded-file Preview / Download share the same helpers as the
  // AdditionalDocuments step so behavior (popup-blocked toast, revoke
  // timing, filename preservation) stays identical across the flow.
  const previewUploaded = (file: File): void => openFileInNewTab(file, t);
  const downloadUploaded = (file: File): void => downloadFileToDevice(file);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {docs.length > 0 && (
        <Paragraph
          strong
          style={{ marginBottom: 0, color: '#111827', fontSize: 14, fontWeight: 600 }}
        >
          {t('formRenderer.pdfGeneration.intro', { count: docs.length })}
        </Paragraph>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        {docs.map((d) => {
          // Only rows with an uploaded template participate in pre-gen; the
          // spinner state is purely visual — the buttons stay clickable
          // throughout and fall back to on-click generation if pressed early.
          const pending = d.previewKey !== null && !(d.entryId in ready);
          return (
            <li
              key={d.entryId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: '#fff',
              }}
            >
              <FilePdfOutlined style={{ color: '#dc2626', fontSize: 22 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#111827',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {d.label}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {pending ? t('formRenderer.pdfGeneration.generating') : d.sourceStepTitle}
                </Text>
              </div>
              <div style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
                <Tooltip title={t('formRenderer.pdfGeneration.previewOne')}>
                  <Button
                    {...ICON_BTN_PROPS}
                    icon={pending ? <LoadingOutlined /> : <EyeOutlined />}
                    onClick={() => void handlePreview(d.label, d.entryId, d.previewKey)}
                  />
                </Tooltip>
                <Tooltip title={t('formRenderer.pdfGeneration.downloadOne')}>
                  <Button
                    {...ICON_BTN_PROPS}
                    type="primary"
                    icon={pending ? <LoadingOutlined /> : <DownloadOutlined />}
                    onClick={() =>
                      void handleDownload(d.label, d.fileName, d.entryId, d.previewKey)
                    }
                  />
                </Tooltip>
              </div>
            </li>
          );
        })}
        {uploadedRows.map((r) => (
          <li
            key={`uploaded:${r.uid}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              background: '#fff',
            }}
          >
            {fileIcon(r.file.name, 22)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#111827',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {r.label}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('formRenderer.pdfGeneration.uploadedSubtitle')}
              </Text>
            </div>
            <div style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
              <Tooltip title={t('formRenderer.pdfGeneration.previewOne')}>
                <Button
                  {...ICON_BTN_PROPS}
                  icon={<EyeOutlined />}
                  onClick={() => previewUploaded(r.file)}
                />
              </Tooltip>
              <Tooltip title={t('formRenderer.pdfGeneration.downloadOne')}>
                <Button
                  {...ICON_BTN_PROPS}
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadUploaded(r.file)}
                />
              </Tooltip>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
